import { Box, Card, CircularProgress, Grid, Tab, Tabs } from "@mui/material";
import React, { SyntheticEvent } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import Layout from "components/Layout";
import FeatureFlags from "components/settings/FeatureFlags";
import Integrations from "components/settings/Integrations";
import ReportCache from "components/settings/ReportCache";
import Secrets from "components/settings/Secrets";
import { useAuth } from "features/store";
import { User, useGetUserQuery } from "features/auth/auth";
import {
  VIEW_SETTINGS_CACHE_TAB_PERMISSION,
  VIEW_SETTINGS_FEATURE_FLAGS_TAB_PERMISSION,
  VIEW_SETTINGS_INTEGRATIONS_TAB_PERMISSION,
  VIEW_SETTINGS_SECRETS_TAB_PERMISSION,
} from "features/auth/permissions";

/**
 * The sub-tabs in display order. `key` is the tab's `?tab=` value in the URL;
 * each tab is shown only to a user holding its `permission`, so a user holding
 * none of them has no Settings at all (the side menu leaves its row out).
 */
const TABS: {
  key: string;
  label: string;
  permission: string;
  content: React.ReactNode;
}[] = [
  {
    key: "feature-flags",
    label: "Feature Flags",
    permission: VIEW_SETTINGS_FEATURE_FLAGS_TAB_PERMISSION,
    content: <FeatureFlags />,
  },
  {
    key: "cache",
    label: "Cache",
    permission: VIEW_SETTINGS_CACHE_TAB_PERMISSION,
    content: <ReportCache />,
  },
  {
    key: "secrets",
    label: "Secrets",
    permission: VIEW_SETTINGS_SECRETS_TAB_PERMISSION,
    content: <Secrets />,
  },
  {
    key: "integrations",
    label: "Integrations",
    permission: VIEW_SETTINGS_INTEGRATIONS_TAB_PERMISSION,
    content: <Integrations />,
  },
];

const SettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading, isAuthenticated } = useAuth();
  // AuthState.user is still `any`; typing it at the slice ripples into
  // consumers outside this feature, so the shape is stated here instead.
  const user: User | null | undefined = useAuth().user;
  // App asks for the user before the session check lands, so on a fresh page
  // load the query briefly holds its own "Not authenticated" CUSTOM_ERROR
  // until checkAuth refetches it. Only an answer from /users/me/ is a failure;
  // counting that placeholder would redirect a signed-in user away.
  const { error: userError } = useGetUserQuery();
  const userFailed =
    userError !== undefined &&
    !("status" in userError && userError.status === "CUSTOM_ERROR");

  // Until the user has loaded nothing is known either way, so neither the
  // settings nor a redirect happens yet. A user that failed to load (or a
  // signed-out visitor, whom the side menu sends to /login) falls through to
  // the redirect rather than spinning forever.
  if (!user && !userFailed && (isLoading || isAuthenticated)) {
    return (
      <Layout title="Settings">
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress aria-label="Loading" />
        </Box>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const permissions = user.permissions ?? [];
  const visibleTabs = TABS.filter((tab) =>
    permissions.includes(tab.permission),
  );
  // The first tab the user can see is the page's own URL.
  const firstTab = visibleTabs[0];
  const tabKey = searchParams.get("tab") ?? firstTab?.key;
  const activeTab = visibleTabs.find((tab) => tab.key === tabKey);

  // A user with no tab, or a typed or bookmarked URL naming a tab the user may
  // not see (or one that does not exist), is sent Home rather than shown
  // another tab.
  if (!activeTab) {
    return <Navigate to="/" replace />;
  }

  const handleTabChange = (_event: SyntheticEvent, key: string) => {
    // The first visible tab is the page's own URL; every other carries its key.
    setSearchParams(key === firstTab.key ? {} : { tab: key });
  };

  return (
    <Layout title="Settings">
      <Card>
        <Tabs value={activeTab.key} onChange={handleTabChange}>
          {visibleTabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </Tabs>
        <Grid container>
          <Grid item xs={12} padding={2}>
            {activeTab.content}
          </Grid>
        </Grid>
      </Card>
    </Layout>
  );
};

export default SettingsPage;
